import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sprout, LogOut, AlertTriangle } from "lucide-react";
import api from "../../lib/api";

const TABS = [
  { id: "blog", label: "Blog" },
  { id: "projects", label: "Projects" },
  { id: "services", label: "Services & pricing" },
  { id: "leads", label: "Leads" },
];

export default function AdminLayout({ tab, onTab, onLogout, children }) {
  const [backendOk, setBackendOk] = useState(true);
  const [backendHint, setBackendHint] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/health")
      .then((r) => {
        if (cancelled) return;
        if (r.data?.cms) {
          setBackendOk(true);
          setBackendHint("");
        } else {
          setBackendOk(false);
          setBackendHint("Backend is running but CMS routes are missing. Restart the API server from the backend folder.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setBackendOk(false);
        const status = err?.response?.status;
        if (status === 404) {
          setBackendHint(
            "The API on port 8000 is an old version without CMS support. Stop it and run: .venv\\Scripts\\uvicorn server:app --host 0.0.0.0 --port 8000 --reload"
          );
        } else {
          setBackendHint("Cannot reach the API. Confirm the backend is running and the site can reach /api/health.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tab]);

  return (
    <div className="min-h-screen bg-background grain">
      <header className="glass sticky top-0 z-40 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-primary text-primary-foreground">
              <Sprout size={18} />
            </span>
            <span className="font-serif-display text-lg hidden sm:inline">Kalpavriksha Admin</span>
          </Link>
          <nav className="flex flex-wrap gap-2 justify-center flex-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`admin-tab-${t.id}`}
                onClick={() => onTab(t.id)}
                className={`text-sm rounded-full px-4 py-2 border transition-colors ${
                  tab === t.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:border-accent"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
          <button
            data-testid="admin-logout"
            type="button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-sm rounded-full border border-border px-4 py-2 hover:border-accent transition-colors shrink-0"
          >
            <LogOut size={16} /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">
        {!backendOk && (
          <div
            role="alert"
            className="mb-8 flex gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-5 py-4 text-sm text-foreground"
          >
            <AlertTriangle className="shrink-0 text-amber-600 mt-0.5" size={20} />
            <div>
              <p className="font-medium">Backend connection issue</p>
              <p className="mt-1 text-muted-foreground">{backendHint}</p>
            </div>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent";

export function AdminField({ label, children }) {
  return (
    <label className="block">
      {label && <span className="text-xs label-caps text-muted-foreground mb-1.5 block">{label}</span>}
      {children}
    </label>
  );
}

export { inputCls };
